import { useEffect, useRef, useState, ChangeEvent } from 'react';
import { createPortal } from 'react-dom';
import { useApp } from '../common/AppProvider';
import { translations } from '../translations/translations';
import { base64SvgToSrc } from '../common/utils';
import { AutomationTask } from './types.ts';
import { ExIcon, ExInput, ExButton, ExCard } from '@boomi/exosphere';
import { useMobile } from './useMobileHook';
import AnimatedLink from '../common/AnimatedLink';

import './filters.css';

const hasMatchingStatus = (filteringStatus: string, task: AutomationTask): boolean =>
    (filteringStatus === 'active' && task.isActive) ||
    (filteringStatus === 'inactive' && !task.isActive) ||
    filteringStatus === 'all';

const TasksFilters = () => {
    const { tasks, connectors, language, setFilteredTasks } = useApp();
    const { isMobile } = useMobile(800);
    const [filterableConnectors, setFilterableConnectors] = useState<string[]>([]);
    const [filteringConnectors, setFilteringConnectors] = useState<string[]>([]);
    const [filteringStatus, setFilteringStatus] = useState<string>('all');
    const [search, setSearch] = useState('');
    const filterWrapper = useRef<HTMLDivElement>(null);
    const filterContainerElement = document.querySelector<HTMLElement>('.automate-root');

    useEffect(() => {
        const connectorRefs: string[] = [];

        tasks.forEach((task) => {
            if (task.trigger) {
                const triggerRef = task.trigger.connectorId;

                if (triggerRef && !connectorRefs.includes(triggerRef)) {
                    connectorRefs.push(triggerRef);
                }

                task.actions.forEach((action) => {
                    const actionRef = action.connectorId;

                    if (actionRef && !connectorRefs.includes(actionRef)) {
                        connectorRefs.push(actionRef);
                    }
                });
            }
        });

        setFilterableConnectors(connectorRefs);
    }, [tasks, connectors]);

    useEffect(() => {
        if (search === '' && filteringConnectors.length === 0 && filteringStatus === 'all') {
            setFilteredTasks([...tasks]);
            return;
        }

        const filteredTasks = tasks.filter((task) => {
            if (task.trigger) {
                if (filteringConnectors.includes(task.trigger.connectorId)) {
                    if (hasMatchingStatus(filteringStatus, task)) {
                        return true;
                    }
                }

                for (const action of task.actions) {
                    if (filteringConnectors.includes(action.connectorId)) {
                        if (hasMatchingStatus(filteringStatus, task)) {
                            return true;
                        }
                    }
                }

                if (filteringConnectors.length === 0 && hasMatchingStatus(filteringStatus, task)) {
                    return true;
                }
            }

            return false;
        });

        const filteredAndSearchedTasks = filteredTasks.filter((task) =>
            task.displayName.toUpperCase().includes(search.toUpperCase()),
        );

        setFilteredTasks(filteredAndSearchedTasks);
    }, [filteringConnectors, tasks, setFilteredTasks, filteringStatus, search]);

    const onToggleFilter = (connectorId: string) => {
        const filterList = [...filteringConnectors];

        if (filterList.includes(connectorId)) {
            const filtered = filterList.filter((c) => c !== connectorId);
            setFilteringConnectors(filtered);
        } else {
            filterList.push(connectorId);
            setFilteringConnectors(filterList);
        }
    };

    const onToggleFiltersPanel = () => {
        filterWrapper?.current?.classList.toggle('is-active');
    };

    const renderFiltersPanel = () => (
        <div className="filter-panel-layout">
            <header className="filter-header">
                <h3 className="title lg">{translations[language].FILTER_filter}</h3>
                <ExButton
                    data-testid="filtersClearButton"
                    className={`filter-clear exo-button-action ${
                        filteringConnectors.length === 0 && filteringStatus === 'all'
                            ? 'is-hidden'
                            : ''
                    }`}
                    flavor="branded"
                    size="large"
                    type="tertiary"
                    onClick={() => {
                        setFilteringConnectors([]);
                        setFilteringStatus('all');
                    }}
                    title={translations[language].FILTER_filter_clear_all}
                >
                    <ExIcon icon="Undo" hide-browser-tooltip />
                </ExButton>
                <ExButton
                    className="filter-close exo-button-action"
                    flavor="branded"
                    size="large"
                    type="tertiary"
                    onClick={onToggleFiltersPanel}
                    title={translations[language].COMMON_close}
                >
                    <ExIcon icon="Close" hide-browser-tooltip />
                </ExButton>
            </header>

            {/* Filter by connectors */}
            <section className="filter-section">
                <label className="label md">
                    {translations[language].FILTER_filter_by_connector}:
                </label>
                <div className="filter-section-content">
                    {filterableConnectors.map((connectorId, index) => (
                        <button
                            className={
                                filteringConnectors.includes(connectorId)
                                    ? 'app-tile is-selected'
                                    : 'app-tile'
                            }
                            key={index}
                            onClick={() => onToggleFilter(connectorId)}
                            title={connectors[connectorId].displayName}
                        >
                            <span className="app-tile-icon-wrapper">
                                <img
                                    src={base64SvgToSrc(connectors[connectorId].icon)}
                                    className="app-tile-icon"
                                    alt={connectors[connectorId].displayName}
                                />
                            </span>
                            <span className="app-tile-text">
                                {connectors[connectorId].displayName}
                            </span>
                        </button>
                    ))}
                </div>
            </section>

            {/* Filter by status */}
            <section className="filter-section">
                <label className="label md">
                    {translations[language].FILTER_filter_by_status}:
                </label>
                <div className="filter-section-content">
                    <label className="filter-status-label">
                        <input
                            type="radio"
                            id="all"
                            name="task_status"
                            value="all"
                            checked={filteringStatus === 'all'}
                            onChange={() => setFilteringStatus('all')}
                        />
                        {translations[language].FILTER_filter_status_all}
                    </label>
                    <label className="filter-status-label">
                        <input
                            type="radio"
                            id="active"
                            name="task_status"
                            value="active"
                            checked={filteringStatus === 'active'}
                            onChange={() => setFilteringStatus('active')}
                        />
                        {translations[language].FILTER_filter_status_active}
                    </label>
                    <label className="filter-status-label">
                        <input
                            type="radio"
                            id="inactive"
                            name="task_status"
                            value="inactive"
                            checked={filteringStatus === 'inactive'}
                            onChange={() => setFilteringStatus('inactive')}
                        />
                        {translations[language].FILTER_filter_status_inactive}
                    </label>
                </div>
            </section>
        </div>
    );

    return (
        <>
            <section className="filter-bar">
                {/* SEARCH */}
                <ExInput
                    data-testId="filterSearchInput"
                    className="exo-search-input"
                    placeholder={translations[language].COMMON_search_placeholder}
                    input-id="default"
                    type="text"
                    inputmode="text"
                    size="default"
                    flavor="default"
                    leadingicon="magnifying-glass"
                    removemarginbottom
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                        setSearch(e.currentTarget.value)
                    }
                    disabled={!tasks.length}
                />

                {/* FILTER TRIGGER */}
                <ExButton
                    data-testId="filterTriggerButton"
                    flavor="base"
                    type="secondary"
                    size="large"
                    onClick={onToggleFiltersPanel}
                    className="exo-button"
                    disabled={!tasks.length}
                >
                    <ExIcon className="filter-trigger-icon" icon="filter" hide-browser-tooltip />
                    {translations[language].FILTER_filter}
                </ExButton>

                {/* NEW TASK */}
                <AnimatedLink className="filters-new-task-button" to="/tasks/new" tabIndex={-1}>
                    <ExButton flavor="base" type="primary" size="large" className="exo-button">
                        <ExIcon
                            className="new-task-icon"
                            icon="plus"
                            slot="prefix"
                            hide-browser-tooltip
                        ></ExIcon>
                        {translations[language].TASKS_add_new}
                    </ExButton>
                </AnimatedLink>
            </section>

            {isMobile && filterContainerElement ? (
                createPortal(
                    <div
                        className="filter-panel filter-panel-mobile"
                        ref={filterWrapper}
                        data-testid="tasksFiltersPanel"
                    >
                        {renderFiltersPanel()}
                    </div>,
                    filterContainerElement,
                )
            ) : (
                <ExCard
                    className="filter-panel filter-panel-desktop"
                    ref={filterWrapper}
                    data-testid="tasksFiltersPanel"
                >
                    {renderFiltersPanel()}
                </ExCard>
            )}
        </>
    );
};

export default TasksFilters;
